import React from 'react'
import { Formik, Form, FormikHelpers } from 'formik'
import { ProfileFormValues } from '../utils'
import { Company } from './Company'
import { Contacts } from './Contacts'
import { useGet } from 'restful-react'
import { endpoints } from 'app/api/endpoints'
import {
  ApiError,
  Locations,
  ProfileFormSteps,
  SupplierUpdateApiError,
} from 'EstraSuppliers'
import { Certifications } from './Certifications'
import { SecondaryAddress } from './SecondaryAddress'
import { Holder } from './Holder'
import { HolderWrapper } from './HolderWrapper'
import { PrimaryBankDetails } from './PrimaryBankDetails'
import { SecondaryBankDetails } from './SecondaryBankDetails'
import { ErrorHandler } from './ErrorHandler'
import { ScrollOnError } from 'app/components/formik/utils'
import styled from 'styled-components'
import { Button } from 'app/components/ui/Button'
import { Text } from 'app/components/ui/Text'
import { Container } from 'app/components/ui/Container'
import { themeEstra } from 'app/styles/themeEstra'
import { FormSection } from 'app/components/shared/FormSection'
import { Base } from 'app/components/ui/Base'
import { Privacy } from './Privacy'

interface Props {
  onSaveDraft: (
    values: ProfileFormValues,
    actions: FormikHelpers<ProfileFormValues>
  ) => void
  userEmail?: string
  initialFormData: ProfileFormValues
  isSavingForm: boolean
  apiError?: SupplierUpdateApiError | null

  activeStep: ProfileFormSteps

  setActiveStep: (step: ProfileFormSteps) => void
}

export const FormProfile: React.FC<Props> = ({
  onSaveDraft,
  initialFormData,
  userEmail,
  isSavingForm,
  apiError,
  activeStep,
  setActiveStep,
}) => {
  // GET USER DATA FROM TOKEN
  const { data: locationsApi } = useGet<Locations, ApiError>({
    path: endpoints.locations,
  })

  const locations: Locations = locationsApi || []

  return (
    <Formik
      initialValues={initialFormData}
      onSubmit={(values, actions) => {
        onSaveDraft(values, actions)
      }}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {() => {
        return (
          <Form>
            {apiError && (
              <>
                <ErrorHandler apiError={apiError} />
                <ScrollOnError apiError={apiError} />
              </>
            )}

            {activeStep === 'user_data' && (
              <StyledBase
                paddingTop={10}
                bgImage='https://res.cloudinary.com/estra/image/upload/v1612880272/estra20dev/area_fornitori/signup/signup_2_bg.svg'
              >
                <Container>
                  <Text variant='s12' mb={2}>
                    (<StyledSpan>*</StyledSpan>) I campi contrassegnati con
                    l&apos;asterisco sono obbligatori.
                  </Text>

                  <FormSection title='Dati di accesso'>
                    <Text
                      color={themeEstra.color.primari.grigioEstra}
                      variant='s16'
                    >
                      Username:
                    </Text>
                    <Text variant='s20' mb={1}>
                      {userEmail}
                    </Text>
                  </FormSection>

                  <FormSection title='Ditta, ente o associazione'>
                    <Company />
                  </FormSection>

                  <FormSection title='Contatti'>
                    <Contacts locations={locations} />
                    <SecondaryAddress locations={locations} />
                  </FormSection>
                </Container>
              </StyledBase>
            )}

            {activeStep === 'accounting_data' && (
              <>
                <StyledBase
                  paddingTop={5}
                  bgImage='https://res.cloudinary.com/estra/image/upload/v1612880468/estra20dev/area_fornitori/signup/signup_3_bg.svg'
                >
                  <Container>
                    <Certifications />
                  </Container>
                </StyledBase>
                <Container>
                  <FormSection title='Titolare / Legale Rappresentante'>
                    <Holder holderWho='holder_address' locations={locations} />
                    <HolderWrapper
                      holderWho='technical_director_address'
                      checkboxText='Il Titolare / Legale Rappresentante è anche il Direttore Tecnico'
                      checkboxFor='fill_technical_director'
                      locations={locations}
                    />
                    <HolderWrapper
                      holderWho='technical_manager_address'
                      checkboxText='Il Titolare / Legale Rappresentante è anche il Responsabile Tecnico'
                      checkboxFor='fill_technical_manager'
                      locations={locations}
                    />
                  </FormSection>
                </Container>
                <StyledBase
                  paddingTop={7}
                  bgImage='https://res.cloudinary.com/estra/image/upload/v1612800100/estra20dev/area_fornitori/signup/signup_4_bg.svg'
                >
                  <Container>
                    <PrimaryBankDetails />

                    <SecondaryBankDetails />
                  </Container>
                </StyledBase>
                <Container>
                  <FormSection>
                    <Privacy />
                  </FormSection>
                </Container>
              </>
            )}

            <FooterWrapper>
              <Text variant='s24' mb={2}>
                A questo punto, se hai compilato TUTTI i campi, cliccando sul
                pulsante “SALVA I DATI” qui sotto, procederai alla pagina
                successiva, altrimenti salverai il modulo in bozza e potrai
                terminare la compilazione più tardi.
              </Text>
              <ButtonsWrapper>
                {activeStep === 'accounting_data' && (
                  <Button
                    size='lg'
                    variant='grigio'
                    onClick={() => setActiveStep('user_data')}
                  >
                    INDIETRO
                  </Button>
                )}
                <Button type='submit' size='lg' disabled={isSavingForm}>
                  SALVA I DATI
                </Button>
              </ButtonsWrapper>
            </FooterWrapper>
          </Form>
        )
      }}
    </Formik>
  )
}

const StyledBase = styled(Base)`
  @media (max-width: ${({ theme }) => theme.bp.desktop}) {
    background-image: none;
  }
`

const StyledSpan = styled.span`
  color: ${({ theme }) => theme.color.secondari.arancioneScuro};
`

const FooterWrapper = styled(Container)`
  display: flex;
  flex-direction: column;
  align-items: center;

  text-align: center;
`

const ButtonsWrapper = styled.div`
  display: flex;

  > * {
    margin: 0rem 1rem;
  }

  @media (max-width: ${({ theme }) => theme.bp.desktop}) {
    flex-direction: column;
    align-items: center;

    > * {
      margin-bottom: 1rem;
    }
  }
`
